import useBreadcrumbs from '@admin/hooks/useAdminBreadcrumbs';
import CoreButton from '@shared/components/core/button/CoreButton';
import Card from '@shared/components/core/card/Card';
import { CardSmallGrid } from '@shared/components/core/card/CommonCardLayouts';
import PageHeader from '@shared/components/core/headers/PageHeader';
import { ContentButton } from '@shared/components/core/text/ContentTypography';
import useCurrentOrgDetailsFetching from '@shared/hooks/useCurrentOrgDetailsFetching';
import { ICardData } from '@shared/models/components/ICoreCardProps';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export default function OrgLicenseActivatedPage() {
    const { t } = useTranslation();
    const { items } = useBreadcrumbs();
    const { licenseType, currentOrg, canViewMembers, canManageBilling, canViewOrgDetails } =
        useCurrentOrgDetailsFetching();
    const navigate = useNavigate();

    const handleContinue = () => {
        navigate('/', { replace: true });
    };

    const guideLink = 'https://cephable.com/for-individuals/tutorials/';

    const cards: ICardData[] = [
        {
            title: 'common:organization.titlePlural',
            description: 'common:organization.description',
            buttons: [
                {
                    text: 'common:organization.viewOrganization',
                    link: '#/org',
                    onCondition: canViewOrgDetails,
                },
                {
                    text: 'common:organization.canViewMembers.title',
                    link: `#/org/${currentOrg?.id}/members`,
                    onCondition: canViewOrgDetails && canViewMembers,
                },
                {
                    text: 'common:organization.canManageBilling.title',
                    link: `#/org/${currentOrg?.id}/billing/redirect/upgrade`,
                    onCondition: canManageBilling,
                },
            ],
        },
        {
            title: 'common:resources.title',
            description: 'common:resources.description',
            buttons: [
                {
                    text: 'common:resources.guides',
                    link: guideLink,
                },
            ],
        },
        {
            title: 'common:feedback.title',
            description: 'common:feedback.description',
            buttons: [
                {
                    text: 'common:feedback.feature',
                    link: 'https://forms.office.com/pages/responsepage.aspx?id=nXUREBN1JUCrY3YZ6NyUAMhvlErPu6pKnaJDLXYRrXdUOVc0M0c2RDBTOVoxUEdSWVUzUkFXMlNMTyQlQCN0PWcu',
                },
                {
                    text: 'common:feedback.bug',
                    link: 'https://forms.office.com/pages/responsepage.aspx?id=nXUREBN1JUCrY3YZ6NyUAMhvlErPu6pKnaJDLXYRrXdUNUIzTFpYSFBTTk5HME1MU1kyQlEyVENVUCQlQCN0PWcu',
                },
                {
                    text: 'common:feedback.contact',
                    link: 'https://cephable.com/contact-us/',
                },
            ],
        },
    ];

    return (
        <>
            <PageHeader
                title={t('common:license.welcome.title', { license: t(`common:billing.${licenseType}.titleLong`) })}
                description={t('common:license.welcome.description', {
                    organization: currentOrg?.name,
                    description: t(`common:billing.${licenseType}.description`),
                })}
                items={items}
            />
            <ContentButton onClick={() => handleContinue()}>{t('tour:onboarding.finish.continueToApp')}</ContentButton>
            <CardSmallGrid>
                {cards.map((card, index) => (
                    <Card key={'card_' + index} title={t(card.title)} description={t(card.description)}>
                        {card.buttons
                            .filter((button) => button.onCondition === undefined || button.onCondition)
                            .map((button, i) => (
                                <CoreButton key={'button_' + index + i} $color="context" href={button.link}>
                                    {t(button.text)}
                                </CoreButton>
                            ))}
                    </Card>
                ))}
            </CardSmallGrid>
        </>
    );
}
